import { Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import keycode from 'keycode';
import { useTranslation } from 'react-i18next';
import { doNothing } from 'remeda';

import SubmitButton from '@@components/buttonImplementations/SubmitButton';
import Icon from '@@components/Icon/Icon';
import ButtonGroup from '@@form/components/ButtonGroup';

import { ModalCloseEventSource, type ModalProps } from './types';

const StyledDialog = styled(Dialog)(({ fullScreen, theme }) => ({
    ...(fullScreen && { padding: theme.spacing(4) }),
}));

const Modal = ({
    type,
    title,
    children,
    isLoading,
    isOpen = true,
    slotProps,
    onClose = doNothing(),
    onDelete = doNothing(),
    onConfirm = doNothing(),
    cancelButtonText,
    confirmButtonText,
    reverseButtonOrder,
    renderTitle,
    ...rest
}: ModalProps) => {
    const { t } = useTranslation();

    // @ts-expect-error TS7006 Issue #4120
    const handleWindowKeyDown = (e) => {
        if (type && isOpen && keycode(e.keyCode) === 'enter') {
            e.preventDefault();

            if (type === 'info' || type === 'confirm') {
                onConfirm();
            } else if (type === 'delete') {
                onDelete();
            }
        }
    };

    // @ts-expect-error TS7006 Issue #4120
    const renderButtons = (type) => {
        if (type === 'info') {
            return (
                <ButtonGroup>
                    <SubmitButton disabled={isLoading} loading={isLoading} onClick={onConfirm}>
                        {confirmButtonText || t('modal.confirmButton')}
                    </SubmitButton>
                </ButtonGroup>
            );
        } else if (type === 'confirm' || type === 'delete') {
            return (
                <ButtonGroup>
                    {!reverseButtonOrder && (
                        <Button
                            // Currently we can not cancel an ongoing request, so the UI
                            // should not be able to do it too.
                            disabled={isLoading}
                            variant="outlined"
                            onClick={() => onClose(ModalCloseEventSource.CANCEL_BUTTON)}
                            autoFocus
                        >
                            {cancelButtonText || t('modal.cancelButton')}
                        </Button>
                    )}

                    {type === 'delete' ? (
                        <Button
                            startIcon={<Icon name="trash-can" />}
                            color="error"
                            loading={isLoading}
                            variant="contained"
                            onClick={onDelete}
                        >
                            {confirmButtonText || t('modal.deleteButton')}
                        </Button>
                    ) : (
                        <SubmitButton loading={isLoading} onClick={onConfirm}>
                            {confirmButtonText || t('modal.confirmButton')}
                        </SubmitButton>
                    )}
                    {reverseButtonOrder && (
                        <Button
                            // Currently we can not cancel an ongoing request, so the UI
                            // should not be able to do it too.
                            disabled={isLoading}
                            variant="outlined"
                            onClick={() => onClose(ModalCloseEventSource.CANCEL_BUTTON)}
                            autoFocus
                        >
                            {cancelButtonText || t('modal.cancelButton')}
                        </Button>
                    )}
                </ButtonGroup>
            );
        }

        return null;
    };

    return (
        <StyledDialog
            open={isOpen}
            aria-labelledby={title}
            onClose={() => {
                onClose();
            }}
            onKeyDown={handleWindowKeyDown}
            {...rest}
        >
            {renderTitle ? renderTitle() : <DialogTitle variant="title3">{title}</DialogTitle>}
            <DialogContent {...slotProps?.content}>{children}</DialogContent>
            {type && <DialogActions>{renderButtons(type)}</DialogActions>}
        </StyledDialog>
    );
};

export default Modal;
